import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  Button,
  Divider,
  Stack,
  Avatar,
  TextField,
  Collapse,
  Checkbox,
  FormControlLabel,
  Rating,
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import EventIcon from '@mui/icons-material/Event';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import { buildEventsApiUrl } from '../config/api';
import { normalizeImageUrl } from '../utils/images';
import type { RootState } from '../store/store';
import { getAuthUserId } from '../utils/authUser';

const refreshMailboxBadge = async () => {
  const refresh = (window as any).__crowdTradesRefreshMailboxBadge;
  if (typeof refresh === 'function') {
    await refresh();
  }
};

const loadProfileShell = async (
  userId: string,
  setProfile: React.Dispatch<React.SetStateAction<TraderProfile | null>>,
  setError?: (value: string | null) => void,
  actingUserId?: string
) => {
  const normalizedRouteUserId = String(userId || '').trim();
  const normalizedActingUserId = String(actingUserId || '').trim();
  const shouldLoadMailbox = Boolean(normalizedActingUserId && normalizedRouteUserId && normalizedActingUserId === normalizedRouteUserId);

  const loadProfileResponse = async () => {
    const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/profile`), {
      headers: actingUserId ? { 'x-user-id': actingUserId } : undefined
    });
    const data = await response.json().catch(() => ({}));
    return { response, data };
  };

  let { response: profileResponse, data: profileData } = await loadProfileResponse();

  if (!profileResponse.ok && profileResponse.status === 404 && shouldLoadMailbox) {
    for (let attempt = 0; attempt < 2 && !profileResponse.ok; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 400 * (attempt + 1)));
      const retryResult = await loadProfileResponse();
      profileResponse = retryResult.response;
      profileData = retryResult.data;
    }
  }

  if (!profileResponse.ok) {
    throw new Error(profileData.error || 'Failed to load trader profile');
  }

  const { mailboxMessages: _ignoredMailboxMessages, mailboxUnreadCount: _ignoredMailboxUnreadCount, ...shellProfile } = profileData.profile || {};

  setProfile((current) => ({
    ...(current || {}),
    ...shellProfile,
    mailboxMessages: shouldLoadMailbox ? (current?.mailboxMessages || []) : [],
    mailboxUnreadCount: shouldLoadMailbox ? (current?.mailboxUnreadCount ?? 0) : 0
  }));
  if (setError) setError(null);
};

const loadMailboxForProfile = async (
  userId: string,
  setProfile: React.Dispatch<React.SetStateAction<TraderProfile | null>>,
  actingUserId?: string
) => {
  const normalizedRouteUserId = String(userId || '').trim();
  const normalizedActingUserId = String(actingUserId || '').trim();
  const shouldLoadMailbox = Boolean(normalizedActingUserId && normalizedRouteUserId && normalizedActingUserId === normalizedRouteUserId);

  if (!shouldLoadMailbox) {
    return;
  }

  try {
    const mailboxResponse = await fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox?includeReplies=false&previewOnly=true`), {
      headers: actingUserId ? { 'x-user-id': actingUserId } : undefined
    });
    const mailboxData = await mailboxResponse.json().catch(() => ({ messages: [], unreadCount: 0 }));

    if (!mailboxResponse.ok) {
      return;
    }

    setProfile((current) => {
      if (!current) return current;
      return {
        ...current,
        mailboxMessages: mailboxData.messages || [],
        mailboxUnreadCount: mailboxData.unreadCount || 0
      };
    });
  } catch (error) {
    console.warn('Failed to refresh mailbox profile data', error);
  }
};

interface TraderItemPreview {
  id: string;
  name: string;
  category?: string;
  condition?: string;
  imageUrl?: string;
}

interface TraderEventPreview {
  id: string;
  eventName: string;
  eventDateTime?: string | null;
  location?: string;
}

interface MailboxReply {
  id: string;
  body?: string;
  createdAt?: string;
  senderDisplayName?: string;
  senderUserID?: string;
  senderTag?: string | null;
  senderTagLabel?: string | null;
  replyAllowed?: boolean;
}

interface MailboxMessage {
  id: string;
  subject?: string;
  body?: string;
  read?: boolean;
  unreadCount?: number;
  createdAt?: string;
  eventName?: string;
  eventID?: string;
  itemName?: string;
  senderDisplayName?: string;
  senderUserID?: string;
  senderTag?: string | null;
  senderTagLabel?: string | null;
  replyAllowed?: boolean;
  reason?: string;
  replyToMessageID?: string;
  threadType?: string;
  childThreadIds?: string[];
  childThreads?: MailboxMessage[];
  replies?: MailboxReply[];
}

interface TraderProfile {
  userID: string;
  displayName: string;
  userName?: string | null;
  avatarUrl?: string | null;
  profileTag?: string | null;
  city?: string | null;
  state?: string | null;
  bio?: string;
  joinedAt?: string | null;
  itemCount: number;
  activeEventCount: number;
  listedItemCount: number;
  outgoingInterestCount: number;
  incomingInterestCount: number;
  trustScore?: number | null;
  completedTradeCount?: number;
  reviewCount?: number;
  mailboxUnreadCount?: number;
  mailboxMessages?: MailboxMessage[];
  items?: TraderItemPreview[];
  activeEvents?: TraderEventPreview[];
  listedEvents?: TraderEventPreview[];
  emailNotificationPreferences?: {
    systemMessages?: boolean;
    eventAdministratorMessages?: boolean;
    traderMessages?: boolean;
    itemInterestAlerts?: boolean;
    eventChangeAlerts?: boolean;
    wantedItemChangeAlerts?: boolean;
    newEventAlerts?: boolean;
  };
}

interface ReputationSummary {
  userId: string;
  reviewCount: number;
  authoredReviewCount: number;
  averageRating: number | null;
  completedSessionReviews: number;
  canceledSessionReviews: number;
  issueReportsFiled: number;
  issuesReportedAgainst: number;
  cleanupRecordsFiled: number;
  completedSessionsInvolved: number;
  canceledSessionsInvolved: number;
  behaviorRecordCount: number;
  noShowCount: number;
  lateArrivalCount: number;
  wrongItemCount: number;
  successfulHandoffCount: number;
  recentReviews?: Array<{ rating: number; reviewStatus?: string; createdAt?: string }>;
}

const formatDate = (value?: string | null) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfMessageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday.getTime() - startOfMessageDay.getTime()) / 86400000);
  const timeLabel = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  if (diffDays === 0) return `Today ${timeLabel}`;
  if (diffDays === 1) return `Yesterday ${timeLabel}`;

  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: 'numeric',
    minute: '2-digit'
  });
};

const TraderProfilePage: React.FC = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const authUser = useSelector((state: RootState) => state.auth.user);
  const authenticatedUserId = getAuthUserId(authUser as any);
  const [profile, setProfile] = useState<TraderProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyingToMessageId, setReplyingToMessageId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [mailboxActionError, setMailboxActionError] = useState<string | null>(null);
  const [mailboxActionBusyId, setMailboxActionBusyId] = useState<string | null>(null);
  const [profileTagDraft, setProfileTagDraft] = useState('');
  const [bioDraft, setBioDraft] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [directMessageSubject, setDirectMessageSubject] = useState('');
  const [directMessageBody, setDirectMessageBody] = useState('');
  const [sendingDirectMessage, setSendingDirectMessage] = useState(false);
  const [directMessageSuccess, setDirectMessageSuccess] = useState<string | null>(null);
  const [emailNotificationPreferencesDraft, setEmailNotificationPreferencesDraft] = useState({
    systemMessages: true,
    eventAdministratorMessages: true,
    traderMessages: true,
    itemInterestAlerts: true,
    eventChangeAlerts: true,
    wantedItemChangeAlerts: true,
    newEventAlerts: true
  });
  const [reputation, setReputation] = useState<ReputationSummary | null>(null);

  const groupedMailboxMessages = useMemo(() => {
    const messages = profile?.mailboxMessages || [];
    const grouped = new Map<string, MailboxMessage>();

    for (const message of messages) {
      const shouldGroupOwnerBroadcast =
        message.threadType === 'event-owner-participant' &&
        message.senderUserID === profile?.userID;

      if (!shouldGroupOwnerBroadcast) {
        grouped.set(message.id, message);
        continue;
      }

      const key = `${message.eventID || 'no-event'}::${message.subject || 'no-subject'}::${message.body || ''}`;
      const existing = grouped.get(key);
      if (!existing) {
        grouped.set(key, {
          ...message,
          id: key,
          senderTag: message.senderTag || null,
          senderTagLabel: message.senderTagLabel || null,
          replyAllowed: message.replyAllowed,
          read: message.read,
          childThreadIds: [message.id],
          childThreads: [message],
          unreadCount: message.unreadCount || 0,
          replies: []
        });
      } else {
        grouped.set(key, {
          ...existing,
          senderTag: existing.senderTag || message.senderTag || null,
          senderTagLabel: existing.senderTagLabel || message.senderTagLabel || null,
          replyAllowed: existing.replyAllowed ?? message.replyAllowed,
          read: Boolean(existing.read && message.read),
          childThreadIds: [...new Set([...(existing.childThreadIds || []), message.id])],
          childThreads: [...(existing.childThreads || []), message].sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()),
          unreadCount: (existing.unreadCount || 0) + (message.unreadCount || 0),
          replies: []
        });
      }
    }

    return Array.from(grouped.values()).sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  }, [profile]);

  const markMailboxMessageRead = async (messageId: string) => {
    if (!userId) return;

    const groupedMessage = groupedMailboxMessages.find((message) => message.id === messageId);
    const targetThreadIds = groupedMessage?.childThreadIds?.length
      ? groupedMessage.childThreadIds
      : [messageId];

    try {
      const responses = await Promise.all(
        targetThreadIds.map((threadId) =>
          fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox/${threadId}/read`), {
            method: 'PATCH',
            headers: authenticatedUserId ? { 'x-user-id': authenticatedUserId } : undefined
          })
        )
      );

      if (responses.some((response) => !response.ok)) return;

      setProfile((current) => {
        if (!current) return current;
        const targetSet = new Set(targetThreadIds);
        const updatedMessages = (current.mailboxMessages || []).map((message) =>
          targetSet.has(message.id)
            ? {
                ...message,
                read: true,
                unreadCount: 0
              }
            : message
        );
        const unreadThreads = updatedMessages.filter((message) => !message.read && (message.unreadCount || 0) > 0).length;
        return {
          ...current,
          mailboxUnreadCount: unreadThreads,
          mailboxMessages: updatedMessages
        };
      });
      await refreshMailboxBadge();
    } catch (err) {
      console.warn('Failed to mark mailbox message as read', err);
    }
  };

  const deleteMailboxMessage = async (messageId: string) => {
    if (!userId) return;

    const groupedMessage = groupedMailboxMessages.find((message) => message.id === messageId);
    const targetThreadIds = groupedMessage?.childThreadIds?.length
      ? groupedMessage.childThreadIds
      : [messageId];

    try {
      setMailboxActionError(null);
      setMailboxActionBusyId(messageId);
      const responses = await Promise.all(
        targetThreadIds.map((threadId) =>
          fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox/${threadId}`), {
            method: 'DELETE',
            headers: authenticatedUserId ? { 'x-user-id': authenticatedUserId } : undefined
          })
        )
      );
      const failedResponse = responses.find((response) => !response.ok);
      const failedData = failedResponse ? await failedResponse.json().catch(() => null) : null;

      if (failedResponse) {
        throw new Error(failedData?.error || 'Failed to delete mailbox message');
      }

      setProfile((current) => {
        if (!current) return current;
        const targetSet = new Set(targetThreadIds);
        const remainingMessages = (current.mailboxMessages || []).filter((message) => !targetSet.has(message.id));
        const unreadThreads = remainingMessages.filter((message) => !message.read && (message.unreadCount || 0) > 0).length;
        return {
          ...current,
          mailboxUnreadCount: unreadThreads,
          mailboxMessages: remainingMessages
        };
      });

      if (replyingToMessageId === messageId) {
        setReplyingToMessageId(null);
        setReplyDraft('');
      }
      await refreshMailboxBadge();
    } catch (err: any) {
      setMailboxActionError(err.message || 'Failed to delete mailbox message');
    } finally {
      setMailboxActionBusyId(null);
    }
  };

  const hydrateMailboxThread = async (messageId: string) => {
    if (!userId) return null;

    const targetThreadId = (profile?.mailboxMessages || []).find((message) => message.id === messageId)?.id ||
      groupedMailboxMessages.find((message) => message.id === messageId)?.childThreadIds?.[0] ||
      messageId;

    const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox/${targetThreadId}`), {
      headers: authenticatedUserId ? { 'x-user-id': authenticatedUserId } : undefined
    });
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.error || 'Failed to load mailbox thread');
    }

    const hydratedThread = data?.message;
    if (!hydratedThread) {
      return targetThreadId;
    }

    setProfile((current) => {
      if (!current) return current;
      return {
        ...current,
        mailboxMessages: (current.mailboxMessages || []).map((message) =>
          message.id === targetThreadId
            ? { ...message, ...hydratedThread }
            : message
        )
      };
    });

    return targetThreadId;
  };

  const replyToMailboxMessage = async (messageId: string) => {
    if (!userId || !replyDraft.trim()) return;

    const targetThreadId = (profile?.mailboxMessages || []).find((message) => message.id === messageId)?.id ||
      groupedMailboxMessages.find((message) => message.id === messageId)?.childThreadIds?.[0] ||
      messageId;

    try {
      setMailboxActionError(null);
      setMailboxActionBusyId(messageId);
      const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox/${targetThreadId}/reply`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authenticatedUserId ? { 'x-user-id': authenticatedUserId } : {})
        },
        body: JSON.stringify({ body: replyDraft.trim() })
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || 'Failed to send reply');
      }

      const optimisticReply = {
        id: data?.message?.id || `optimistic-${Date.now()}`,
        body: replyDraft.trim(),
        createdAt: data?.message?.createdAt || new Date().toISOString(),
        senderDisplayName: profile?.displayName || 'You',
        senderUserID: profile?.userID || userId
      };

      setProfile((current) => {
        if (!current) return current;
        return {
          ...current,
          mailboxMessages: (current.mailboxMessages || []).map((message) =>
            message.id === messageId || message.id === targetThreadId
              ? {
                  ...message,
                  read: true,
                  unreadCount: 0,
                  replies: [...(message.replies || []), optimisticReply]
                }
              : message
          )
        };
      });

      await markMailboxMessageRead(messageId);
      setReplyingToMessageId(null);
      setReplyDraft('');
      await refreshMailboxBadge();
    } catch (err: any) {
      setMailboxActionError(err.message || 'Failed to send reply');
    } finally {
      setMailboxActionBusyId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async ({ showLoading = false }: { showLoading?: boolean } = {}) => {
      if (!userId) return;
      try {
        if (showLoading) setLoading(true);

        const profilePromise = loadProfileShell(userId, (next) => {
          if (!cancelled) setProfile(next);
        }, (value) => {
          if (!cancelled) setError(value);
        }, authenticatedUserId);

        const reputationPromise = fetch(buildEventsApiUrl(`/api/users/${userId}/reputation`))
          .then(async (reputationResponse) => {
            const reputationData = await reputationResponse.json();
            if (!reputationResponse.ok) {
              throw new Error(reputationData.error || 'Failed to load trader reputation');
            }
            if (!cancelled) setReputation(reputationData.reputation || null);
          })
          .catch((reputationError: any) => {
            console.warn('Failed to load trader reputation', reputationError);
          });

        await profilePromise;

        if (!cancelled && showLoading) {
          setLoading(false);
        }

        await reputationPromise;
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Failed to load trader profile');
          if (showLoading) {
            setLoading(false);
          }
        }
      }
    };

    loadProfile({ showLoading: true });

    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        loadProfile();
      }
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadProfile();
      }
    }, 30000);

    document.addEventListener('visibilitychange', refreshIfVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [userId, authenticatedUserId]);

  useEffect(() => {
    const normalizedRouteUserId = String(userId || '').trim();
    const normalizedAuthUserId = String(authenticatedUserId || '').trim();
    const shouldLoadMailbox = Boolean(
      profile &&
      normalizedRouteUserId &&
      normalizedAuthUserId &&
      normalizedRouteUserId === normalizedAuthUserId
    );

    if (!shouldLoadMailbox) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      loadMailboxForProfile(userId!, (next) => setProfile(next), authenticatedUserId);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [userId, authenticatedUserId, profile]);

  useEffect(() => {
    setProfileTagDraft(profile?.profileTag || '');
    setBioDraft(profile?.bio || '');
    setEmailNotificationPreferencesDraft({
      systemMessages: profile?.emailNotificationPreferences?.systemMessages !== false,
      eventAdministratorMessages: profile?.emailNotificationPreferences?.eventAdministratorMessages !== false,
      traderMessages: profile?.emailNotificationPreferences?.traderMessages !== false,
      itemInterestAlerts: profile?.emailNotificationPreferences?.itemInterestAlerts !== false,
      eventChangeAlerts: profile?.emailNotificationPreferences?.eventChangeAlerts !== false,
      wantedItemChangeAlerts: profile?.emailNotificationPreferences?.wantedItemChangeAlerts !== false,
      newEventAlerts: profile?.emailNotificationPreferences?.newEventAlerts !== false
    });
  }, [
    profile?.userID,
    profile?.profileTag,
    profile?.bio,
    profile?.emailNotificationPreferences?.systemMessages,
    profile?.emailNotificationPreferences?.eventAdministratorMessages,
    profile?.emailNotificationPreferences?.traderMessages,
    profile?.emailNotificationPreferences?.itemInterestAlerts,
    profile?.emailNotificationPreferences?.eventChangeAlerts,
    profile?.emailNotificationPreferences?.wantedItemChangeAlerts,
    profile?.emailNotificationPreferences?.newEventAlerts
  ]);

  const normalizedAuthenticatedUserId = String(authenticatedUserId || '').trim();
  const normalizedRouteUserId = String(userId || '').trim();
  const normalizedProfileUserId = String(profile?.userID || profile?.userName || '').trim();
  const normalizedProfileUserIdWithoutPrefix = normalizedProfileUserId.startsWith('profile_')
    ? normalizedProfileUserId.slice('profile_'.length)
    : normalizedProfileUserId;
  const normalizedProfileIdField = String((profile as any)?.id || '').trim();
  const normalizedProfileIdFieldWithoutPrefix = normalizedProfileIdField.startsWith('profile_')
    ? normalizedProfileIdField.slice('profile_'.length)
    : normalizedProfileIdField;

  const isOwnProfile = Boolean(
    normalizedAuthenticatedUserId && (
      normalizedProfileUserId === normalizedAuthenticatedUserId ||
      normalizedProfileUserIdWithoutPrefix === normalizedAuthenticatedUserId ||
      normalizedProfileIdField === normalizedAuthenticatedUserId ||
      normalizedProfileIdFieldWithoutPrefix === normalizedAuthenticatedUserId ||
      normalizedRouteUserId === normalizedAuthenticatedUserId
    )
  );


  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '50vh', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !profile) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">{error || 'Trader profile not found'}</Alert>
      </Container>
    );
  }

  if (typeof window !== 'undefined') {
    (window as any).__profileDebug = {
      authenticatedUserId: normalizedAuthenticatedUserId,
      routeUserId: normalizedRouteUserId,
      profileUserId: normalizedProfileUserId,
      profileUserIdWithoutPrefix: normalizedProfileUserIdWithoutPrefix,
      isOwnProfile,
      displayName: profile.displayName,
    };
  }

  const saveProfileDetails = async () => {
    if (!userId) return;
    try {
      setSavingProfile(true);
      const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/profile`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': authenticatedUserId
        },
        body: JSON.stringify({
          bio: bioDraft,
          profileTag: profileTagDraft,
          emailNotificationPreferences: emailNotificationPreferencesDraft
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update profile');
      }
      setProfile((current) => current ? { ...current, ...data.profile, bio: bioDraft, profileTag: profileTagDraft } : current);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const uploadAvatar = async (file?: File | null) => {
    if (!userId || !file) return;
    try {
      setUploadingAvatar(true);
      const formData = new FormData();
      formData.append('image', file);
      const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/upload-avatar`), {
        method: 'POST',
        headers: {
          'x-user-id': authenticatedUserId
        },
        body: formData
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload avatar');
      }
      setProfile((current) => current ? { ...current, avatarUrl: normalizeImageUrl(data.avatarUrl) } : current);
    } catch (err: any) {
      setError(err.message || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const sendDirectMessage = async () => {
    if (!userId || !authenticatedUserId || !directMessageBody.trim()) return;

    try {
      setSendingDirectMessage(true);
      setMailboxActionError(null);
      setDirectMessageSuccess(null);

      const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authenticatedUserId ? { 'x-user-id': authenticatedUserId } : {})
        },
        body: JSON.stringify({
          senderUserId: authenticatedUserId,
          subject: directMessageSubject.trim() || `Message from ${authUser?.displayName || 'a trader'}`,
          body: directMessageBody.trim()
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      setDirectMessageBody('');
      setDirectMessageSubject('');
      setDirectMessageSuccess('Message sent to this trader.');
    } catch (err: any) {
      setMailboxActionError(err.message || 'Failed to send message');
    } finally {
      setSendingDirectMessage(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 3 }}>
        Back
      </Button>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
                <Stack spacing={1} alignItems="center">
                  <Avatar src={normalizeImageUrl(profile.avatarUrl) || undefined} sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
                    <PersonIcon sx={{ fontSize: 36 }} />
                  </Avatar>
                  {isOwnProfile && (
                    <Button component="label" size="small" variant="outlined" disabled={uploadingAvatar} sx={{ textTransform: 'none' }}>
                      {uploadingAvatar ? 'Uploading...' : 'Upload photo'}
                      <input hidden type="file" accept="image/*" onChange={(e) => uploadAvatar(e.target.files?.[0] || null)} />
                    </Button>
                  )}
                </Stack>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {profile.displayName}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <Typography color="text.secondary">
                      {profile.userName ? `@${profile.userName}` : profile.userID}
                    </Typography>
                    {profile.profileTag && <Chip size="small" label={profile.profileTag} color="secondary" />}
                  </Stack>
                </Box>
              </Box>

              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                data-testid="profile-stat-chips"
                sx={{ mb: 2 }}
              >
                <Chip label={`Items listed: ${Number(profile.listedItemCount || 0)}`} icon={<Inventory2Icon />} />
                <Chip label={`Offers received: ${Number(profile.incomingInterestCount || 0)}`} />
                <Chip label={`Active events: ${Number(profile.activeEventCount || 0)}`} icon={<EventIcon />} />
                <Chip label={`Offers made: ${Number(profile.outgoingInterestCount || 0)}`} />
              </Stack>

              {reputation && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 3, backgroundColor: '#f8fbff' }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <HandshakeOutlinedIcon sx={{ color: '#667eea' }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Event-day reputation
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
                      <Box>
                        <Typography variant="body2" color="text.secondary">Average rating</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Rating value={reputation.averageRating || 0} precision={0.1} readOnly />
                          <Typography sx={{ fontWeight: 700 }}>{reputation.averageRating ?? '-'}</Typography>
                        </Stack>
                      </Box>
                      <Chip label={`Reviews: ${reputation.reviewCount}`} icon={<StarBorderIcon />} />
                      <Chip label={`Reviews authored: ${reputation.authoredReviewCount}`} />
                      <Chip label={`Completed-session reviews: ${reputation.completedSessionReviews}`} />
                      <Chip label={`Canceled-session reviews: ${reputation.canceledSessionReviews}`} />
                    </Stack>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Chip color="warning" variant="outlined" label={`Issues filed: ${reputation.issueReportsFiled}`} />
                      <Chip color="warning" variant="outlined" label={`Issues reported against: ${reputation.issuesReportedAgainst}`} />
                      <Chip variant="outlined" label={`Cleanup records filed: ${reputation.cleanupRecordsFiled}`} />
                      <Chip variant="outlined" label={`Completed sessions: ${reputation.completedSessionsInvolved}`} />
                      <Chip variant="outlined" label={`Canceled sessions: ${reputation.canceledSessionsInvolved}`} />
                      <Chip variant="outlined" label={`Behavior records: ${reputation.behaviorRecordCount}`} />
                      <Chip color="success" variant="outlined" label={`Successful handoffs: ${reputation.successfulHandoffCount}`} />
                      <Chip color="warning" variant="outlined" label={`No-shows: ${reputation.noShowCount}`} />
                      <Chip color="warning" variant="outlined" label={`Late arrivals: ${reputation.lateArrivalCount}`} />
                      <Chip color="warning" variant="outlined" label={`Wrong item reports: ${reputation.wrongItemCount}`} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Early reputation summary from event-day reviews and operational logs. Useful signal, not divine truth.
                    </Typography>
                    {(reputation.recentReviews || []).length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Recent received reviews</Typography>
                        <Stack spacing={1}>
                          {(reputation.recentReviews || []).map((review, index) => (
                            <Paper key={`${review.createdAt || 'review'}-${index}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Rating value={review.rating || 0} readOnly size="small" />
                                  <Typography variant="body2" color="text.secondary">{review.reviewStatus || 'review'}</Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">{formatDate(review.createdAt)}</Typography>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              )}

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Joined</Typography>
                  <Typography>{formatDate(profile.joinedAt)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Trader ID</Typography>
                  <Typography sx={{ wordBreak: 'break-all' }}>{profile.userID}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">About</Typography>
                  <Typography color={profile.bio ? 'text.primary' : 'text.secondary'}>
                    {profile.bio || 'No trader bio yet.'}
                  </Typography>
                </Grid>
                {isOwnProfile && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1.5, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'rgba(102,126,234,0.03)' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Edit your profile
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Update your public tag, bio, and profile photo.
                      </Typography>
                      <Stack spacing={2}>
                        <TextField
                          label="Profile tag"
                          value={profileTagDraft}
                          onChange={(e) => setProfileTagDraft(e.target.value.slice(0, 40))}
                          helperText="Small label shown next to your name"
                          fullWidth
                        />
                        <TextField
                          label="Bio"
                          value={bioDraft}
                          onChange={(e) => setBioDraft(e.target.value)}
                          multiline
                          minRows={3}
                          fullWidth
                        />
                        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Email notifications
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            These control personal email delivery only. Your CrowdTrades inbox still receives all messages.
                          </Typography>
                          <Stack spacing={0.5}>
                            <FormControlLabel
                              control={<Checkbox checked={emailNotificationPreferencesDraft.systemMessages} onChange={(e) => setEmailNotificationPreferencesDraft((current) => ({ ...current, systemMessages: e.target.checked }))} />}
                              label="System messages"
                            />
                            <FormControlLabel
                              control={<Checkbox checked={emailNotificationPreferencesDraft.eventAdministratorMessages} onChange={(e) => setEmailNotificationPreferencesDraft((current) => ({ ...current, eventAdministratorMessages: e.target.checked }))} />}
                              label="Event administrator messages"
                            />
                            <FormControlLabel
                              control={<Checkbox checked={emailNotificationPreferencesDraft.traderMessages} onChange={(e) => setEmailNotificationPreferencesDraft((current) => ({ ...current, traderMessages: e.target.checked }))} />}
                              label="Messages from other traders"
                            />
                            <FormControlLabel
                              control={<Checkbox checked={(emailNotificationPreferencesDraft as any).itemInterestAlerts} onChange={(e) => setEmailNotificationPreferencesDraft((current) => ({ ...current, itemInterestAlerts: e.target.checked }))} />}
                              label="Interest in my items"
                            />
                            <FormControlLabel
                              control={<Checkbox checked={(emailNotificationPreferencesDraft as any).eventChangeAlerts} onChange={(e) => setEmailNotificationPreferencesDraft((current) => ({ ...current, eventChangeAlerts: e.target.checked }))} />}
                              label="Event changes for events I'm in"
                            />
                            <FormControlLabel
                              control={<Checkbox checked={(emailNotificationPreferencesDraft as any).wantedItemChangeAlerts} onChange={(e) => setEmailNotificationPreferencesDraft((current) => ({ ...current, wantedItemChangeAlerts: e.target.checked }))} />}
                              label="Changes to items on my want list"
                            />
                            <FormControlLabel
                              control={<Checkbox checked={Boolean((emailNotificationPreferencesDraft as any).newEventAlerts)} onChange={(e) => setEmailNotificationPreferencesDraft((current) => ({ ...current, newEventAlerts: e.target.checked }))} />}
                              label="New event alerts near my selected area"
                            />
                          </Stack>
                        </Box>
                        <Box>
                          <Button variant="contained" onClick={saveProfileDetails} disabled={savingProfile} sx={{ textTransform: 'none' }}>
                            {savingProfile ? 'Saving...' : 'Save Profile'}
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Stack spacing={3}>
            {!isOwnProfile && authenticatedUserId && (
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Message this trader
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Send a direct mailbox message to {profile.displayName}.
                  </Typography>
                  {directMessageSuccess && <Alert severity="success" sx={{ mb: 2 }}>{directMessageSuccess}</Alert>}
                  {mailboxActionError && <Alert severity="error" sx={{ mb: 2 }}>{mailboxActionError}</Alert>}
                  <Stack spacing={2}>
                    <TextField
                      label="Subject"
                      value={directMessageSubject}
                      onChange={(e) => setDirectMessageSubject(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Message"
                      value={directMessageBody}
                      onChange={(e) => setDirectMessageBody(e.target.value)}
                      multiline
                      minRows={4}
                      fullWidth
                      placeholder="Say hello, ask about a trade, or start the conversation here..."
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={sendDirectMessage}
                        disabled={!directMessageBody.trim() || sendingDirectMessage}
                        sx={{ textTransform: 'none' }}
                      >
                        {sendingDirectMessage ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {isOwnProfile && (
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Mailbox
                  </Typography>
                </Box>
                {mailboxActionError && <Alert severity="error" sx={{ mb: 2 }}>{mailboxActionError}</Alert>}
                {groupedMailboxMessages.length > 0 ? (
                  <Stack spacing={1.5}>
                    {groupedMailboxMessages.map((message) => (
                      <Box
                        key={message.id}
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: message.read ? 'divider' : 'primary.main',
                          borderRadius: 2,
                          bgcolor: message.read ? 'transparent' : 'rgba(102,126,234,0.06)'
                        }}
                      >
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography sx={{ fontWeight: 600 }}>{message.subject || 'Message'}</Typography>
                          {message.senderTagLabel && (
                            <Chip
                              size="small"
                              label={message.senderTagLabel}
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                backgroundColor: message.senderTag === 'system' ? '#374151' : '#ede9fe',
                                color: message.senderTag === 'system' ? 'white' : '#6d28d9'
                              }}
                            />
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {message.body || 'No message body'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          {[message.senderDisplayName, message.eventName, formatDate(message.createdAt)].filter(Boolean).join(' • ')}
                        </Typography>
                        {message.childThreads && message.childThreads.length > 0 ? (
                          <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                            {message.childThreads
                              .filter((thread) => (thread.replies || []).length > 0)
                              .map((thread) => (
                                <Box
                                  key={thread.id}
                                  sx={{
                                    border: '1px solid',
                                    borderColor: thread.read ? 'divider' : 'primary.main',
                                    borderRadius: 2,
                                    p: 1.5,
                                    ml: 2,
                                    bgcolor: thread.read ? 'rgba(0,0,0,0.01)' : 'rgba(102,126,234,0.05)'
                                  }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {thread.replies?.find((reply) => reply.senderUserID !== profile.userID)?.senderDisplayName || 'Participant'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                    {thread.read ? 'Read' : 'Unread'} • {formatDate(thread.createdAt)}
                                  </Typography>
                                  <Stack spacing={1} sx={{ mt: 1.25, pl: 2, borderLeft: '3px solid #c5cae9' }}>
                                    {(thread.replies || []).map((reply) => (
                                      <Box
                                        key={reply.id}
                                        sx={{
                                          p: 1.25,
                                          borderRadius: 2,
                                          bgcolor: reply.senderUserID === profile.userID ? 'rgba(102,126,234,0.12)' : 'rgba(76,175,80,0.10)'
                                        }}
                                      >
                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {reply.senderUserID === profile.userID ? 'You replied' : `${reply.senderDisplayName || 'Participant'} replied`}
                                          </Typography>
                                          {reply.senderTagLabel && (
                                            <Chip
                                              size="small"
                                              label={reply.senderTagLabel}
                                              sx={{
                                                height: 20,
                                                fontWeight: 700,
                                                backgroundColor: reply.senderTag === 'system' ? '#374151' : '#ede9fe',
                                                color: reply.senderTag === 'system' ? 'white' : '#6d28d9'
                                              }}
                                            />
                                          )}
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                          {reply.body || 'No reply body'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                                          {formatDate(reply.createdAt)}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                  <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
                                    {!thread.read && (
                                      <Button size="small" onClick={() => markMailboxMessageRead(thread.id)}>
                                        Mark read
                                      </Button>
                                    )}
                                    {thread.replyAllowed !== false && (
                                      <Button
                                        size="small"
                                        onClick={() => {
                                          setMailboxActionError(null);
                                          const nextReplyId = replyingToMessageId === thread.id ? null : thread.id;
                                          setReplyingToMessageId(nextReplyId);
                                          setReplyDraft('');
                                          if (nextReplyId) {
                                            hydrateMailboxThread(thread.id).catch((error) => {
                                              console.warn('Failed to hydrate mailbox thread', error);
                                            });
                                          }
                                        }}
                                      >
                                        {replyingToMessageId === thread.id ? 'Cancel reply' : 'Reply'}
                                      </Button>
                                    )}
                                    <Button
                                      size="small"
                                      color="error"
                                      onClick={() => deleteMailboxMessage(thread.id)}
                                      disabled={mailboxActionBusyId === thread.id}
                                    >
                                      Delete
                                    </Button>
                                  </Stack>
                                  <Collapse in={thread.replyAllowed !== false && replyingToMessageId === thread.id}>
                                    <Box sx={{ mt: 1.5 }}>
                                      <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        label="Reply"
                                        value={replyDraft}
                                        onChange={(e) => setReplyDraft(e.target.value)}
                                        placeholder="Send a mailbox reply back to this trader..."
                                      />
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                        <Button
                                          variant="contained"
                                          onClick={() => replyToMailboxMessage(thread.id)}
                                          disabled={!replyDraft.trim() || mailboxActionBusyId === thread.id}
                                        >
                                          Send Reply
                                        </Button>
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </Box>
                              ))}
                            {message.childThreads.filter((thread) => (thread.replies || []).length === 0).length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                {message.childThreads.filter((thread) => (thread.replies || []).length === 0).length} participant threads have no replies yet.
                              </Typography>
                            )}
                          </Stack>
                        ) : message.replies && message.replies.length > 0 && (
                          <Stack spacing={1} sx={{ mt: 1.5, ml: 2.5, pl: 2, borderLeft: '3px solid #c5cae9' }}>
                            {message.replies.map((reply) => (
                              <Box
                                key={reply.id}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  bgcolor: reply.senderUserID === profile.userID ? 'rgba(102,126,234,0.12)' : 'rgba(76,175,80,0.10)'
                                }}
                              >
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {reply.senderUserID === profile.userID ? 'You replied' : `${reply.senderDisplayName || 'Participant'} replied`}
                                  </Typography>
                                  {reply.senderTagLabel && (
                                    <Chip
                                      size="small"
                                      label={reply.senderTagLabel}
                                      sx={{
                                        height: 20,
                                        fontWeight: 700,
                                        backgroundColor: reply.senderTag === 'system' ? '#374151' : '#ede9fe',
                                        color: reply.senderTag === 'system' ? 'white' : '#6d28d9'
                                      }}
                                    />
                                  )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                  {reply.body || 'No reply body'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                                  {formatDate(reply.createdAt)}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                          {!message.read && (
                            <Button size="small" onClick={() => markMailboxMessageRead(message.id)}>
                              Mark all read
                            </Button>
                          )}
                          {!message.childThreads && (
                            <>
                              {message.replyAllowed !== false && (
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setMailboxActionError(null);
                                    const nextReplyId = replyingToMessageId === message.id ? null : message.id;
                                    setReplyingToMessageId(nextReplyId);
                                    setReplyDraft('');
                                    if (nextReplyId) {
                                      hydrateMailboxThread(message.id).catch((error) => {
                                        console.warn('Failed to hydrate mailbox thread', error);
                                      });
                                    }
                                  }}
                                >
                                  {replyingToMessageId === message.id ? 'Cancel reply' : 'Reply'}
                                </Button>
                              )}
                              <Button
                                size="small"
                                color="error"
                                onClick={() => deleteMailboxMessage(message.id)}
                                disabled={mailboxActionBusyId === message.id}
                              >
                                Delete
                              </Button>
                            </>
                          )}
                          {message.childThreads && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => deleteMailboxMessage(message.id)}
                              disabled={mailboxActionBusyId === message.id}
                            >
                              Delete all
                            </Button>
                          )}
                        </Stack>
                        {!message.childThreads && (
                          <Collapse in={message.replyAllowed !== false && replyingToMessageId === message.id}>
                            <Box sx={{ mt: 1.5 }}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Reply"
                                value={replyDraft}
                                onChange={(e) => setReplyDraft(e.target.value)}
                                placeholder="Send a mailbox reply back to this trader..."
                              />
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                <Button
                                  variant="contained"
                                  onClick={() => replyToMailboxMessage(message.id)}
                                  disabled={!replyDraft.trim() || mailboxActionBusyId === message.id}
                                >
                                  Send Reply
                                </Button>
                              </Box>
                            </Box>
                          </Collapse>
                        )}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">No mailbox messages yet.</Typography>
                )}
              </CardContent>
            </Card>
            )}

          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default TraderProfilePage;
